import React from 'react';
import { SuccessStoryModel } from '../../models';

const maskName = (name: string) => {
    if (!name) return '';
    if (name.length <= 1) return name;
    if (name.length === 2) return name[0] + 'O';
    return name[0] + 'O'.repeat(name.length - 2) + name[name.length - 1];
};

interface LaurelSuccessCardProps {
    story: SuccessStoryModel;
}

export const LaurelSuccessCard: React.FC<LaurelSuccessCardProps> = ({ story }) => {
    const maskedName = maskName(story.studentName);
    const shortSchool = (story.school || '').replace('고등학교', '고').replace('중학교', '중').replace('초등학교', '초');

    let line1 = ''; // Achievement
    let line2 = maskedName; // Student Name
    let line3 = `${shortSchool} ${story.grade || ''}`; // School & Grade
    let subjectText = ''; // Subject or Department under trophy

    if (story.type === 'university' || story.type === 'high_school') {
        line1 = `${story.admissionSchool || ''} 합격`;
        subjectText = story.department || '';
    } else if (story.type === 'grade') {
        const displayScore = story.score ? (story.score.includes('점') ? story.score : `${story.score}점`) : '';
        line1 = `${displayScore} 달성`;
        const examName = `${story.year ? `${story.year}년 ` : ''}${story.semester ? `${story.semester} ` : ''}`;
        subjectText = `${examName}${story.subject || ''}`;
    } else if (story.type === 'rank') {
        const displayRank = story.rank ? (story.rank.includes('등급') ? story.rank : `${story.rank}등급`) : '';
        line1 = `${displayRank} 달성`;
        const examName = `${story.year ? `${story.year}년 ` : ''}${story.semester ? `${story.semester} ` : ''}`;
        subjectText = `${examName}${story.subject || ''}`;
    } else if (story.type === 'standing') {
        const rawStanding = story.standing || story.rank || '';
        const displayStanding = rawStanding ? (rawStanding.includes('등') ? rawStanding : `${rawStanding}등`) : '';
        line1 = `${displayStanding} 달성`;
        const examName = `${story.year ? `${story.year}년 ` : ''}${story.semester ? `${story.semester} ` : ''}`;
        subjectText = `${examName}${story.subject || ''}`;
    } else if (story.type === 'improvement') {
        const rawImp = story.improvementScore || '';
        const displayImp = rawImp ? (rawImp.includes('점') || rawImp.includes('등급') || rawImp.includes('향상') || rawImp.includes('상승') ? rawImp : `${rawImp}점 향상`) : '';
        line1 = displayImp;
        const examName = `${story.year ? `${story.year}년 ` : ''}${story.semester ? `${story.semester} ` : ''}`;
        subjectText = `${examName}${story.subject || ''}`;
    }

    // Mathematical Wreath Circle/Ellipse Arc points
    const leavesCount = 7;
    const leftLeaves = [];
    const rightLeaves = [];
    const Rx = 46; // Radius X (stretched horizontally)
    const Ry = 41; // Radius Y

    const leftBaseAngles = [-45, -30, -15, 0, 15, 30, 45];
    const rightBaseAngles = [45, 30, 15, 0, -15, -30, -45];

    for (let i = 0; i < leavesCount; i++) {
        const t = 0.53 * Math.PI + (i / (leavesCount - 1)) * (0.80 * Math.PI);
        const x = 50 + Rx * Math.cos(t);
        const y = 50 + Ry * Math.sin(t);
        leftLeaves.push({ x, y, phi: leftBaseAngles[i] });
    }

    for (let i = 0; i < leavesCount; i++) {
        const t = 0.47 * Math.PI - (i / (leavesCount - 1)) * (0.80 * Math.PI);
        const x = 50 + Rx * Math.cos(t);
        const y = 50 + Ry * Math.sin(t);
        rightLeaves.push({ x, y, phi: rightBaseAngles[i] });
    }

    return (
        <div className="relative flex flex-col items-center justify-center p-2 bg-gradient-to-br from-[#182a54] via-[#0d1630] to-[#050917] rounded-[2rem] border border-indigo-500/25 group-hover:border-amber-500/50 hover:shadow-amber-500/5 transition-all duration-300 aspect-square w-full select-none shadow-2xl overflow-hidden">
            {/* Subtle Golden Glow inside the card */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.06)_0%,transparent_65%)] pointer-events-none rounded-[2rem]" />
            {/* Laurel SVG */}
            <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[98%] h-[94%] text-amber-500 transition-colors duration-300" viewBox="0 0 100 100">
                <defs>
                    <linearGradient id="goldGradient" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#92400e" />
                        <stop offset="25%" stopColor="#f59e0b" />
                        <stop offset="50%" stopColor="#fef08a" />
                        <stop offset="75%" stopColor="#fbbf24" />
                        <stop offset="100%" stopColor="#78350f" />
                    </linearGradient>
                    <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="1.2" stdDeviation="1.0" floodColor="#000000" floodOpacity="0.9" />
                    </filter>
                </defs>

                {/* Ellipse matched gold branch stems crossing at bottom and ending precisely at topmost leaf pair */}
                <path d="M 59,93 L 47,88.5 A 46,41 0 0,1 26.5,14.7" fill="none" stroke="url(#goldGradient)" strokeWidth="1.2" opacity="0.85" filter="url(#dropShadow)" />
                <path d="M 41,93 L 53,88.5 A 46,41 0 0,0 73.5,14.7" fill="none" stroke="url(#goldGradient)" strokeWidth="1.2" opacity="0.85" filter="url(#dropShadow)" />

                {/* Left Leaves */}
                {leftLeaves.map((node, idx) => (
                    <g key={`l-${idx}`} transform={`translate(${node.x}, ${node.y})`} filter="url(#dropShadow)">
                        <g transform={`rotate(${node.phi - 28})`}>
                            <path d="M0,0 C-2,-4 -4,-8 0,-13 C4,-8 2,-4 0,0" fill="url(#goldGradient)" />
                            <path d="M0,0 L0,-11" stroke="#78350f" strokeWidth="0.3" opacity="0.3" />
                        </g>
                        <g transform={`rotate(${node.phi + 28})`}>
                            <path d="M0,0 C-2,-4 -4,-8 0,-13 C4,-8 2,-4 0,0" fill="url(#goldGradient)" />
                            <path d="M0,0 L0,-11" stroke="#78350f" strokeWidth="0.3" opacity="0.3" />
                        </g>
                    </g>
                ))}

                {/* Right Leaves */}
                {rightLeaves.map((node, idx) => (
                    <g key={`r-${idx}`} transform={`translate(${node.x}, ${node.y})`} filter="url(#dropShadow)">
                        <g transform={`rotate(${node.phi - 28})`}>
                            <path d="M0,0 C-2,-4 -4,-8 0,-13 C4,-8 2,-4 0,0" fill="url(#goldGradient)" />
                            <path d="M0,0 L0,-11" stroke="#78350f" strokeWidth="0.3" opacity="0.3" />
                        </g>
                        <g transform={`rotate(${node.phi + 28})`}>
                            <path d="M0,0 C-2,-4 -4,-8 0,-13 C4,-8 2,-4 0,0" fill="url(#goldGradient)" />
                            <path d="M0,0 L0,-11" stroke="#78350f" strokeWidth="0.3" opacity="0.3" />
                        </g>
                    </g>
                ))}
            </svg>

            {/* Content Overlay - Shifted slightly upwards for visual center alignment */}
            <div className="absolute top-[46%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center justify-center text-center space-y-0.5 w-[88%] h-[82%]">
                {/* Golden Trophy Icon */}
                <svg className="w-6 h-6 filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)] mb-0.5" viewBox="0 0 24 24" fill="url(#goldGradient)">
                    <path d="M18 2H6a1 1 0 0 0-1 1v5c0 3.31 2.69 6 6 6h2c3.31 0 6-2.69 6-6V3a1 1 0 0 0-1-1z" />
                    <path d="M5 4H3a1 1 0 0 0-1 1v2c0 1.66 1.34 3 3 3h0" stroke="url(#goldGradient)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                    <path d="M19 4h2a1 1 0 0 1 1 1v2c0 1.66-1.34 3-3 3h0" stroke="url(#goldGradient)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                    <path d="M12 14v4M8 18h8M10 22h4" stroke="url(#goldGradient)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>

                {/* Subject / Department (Under the trophy) */}
                {subjectText && (
                    <span className="text-[10px] sm:text-[11px] font-bold text-amber-500/85 tracking-wider uppercase truncate max-w-full">
                        {subjectText}
                    </span>
                )}

                {/* Line 1: Achievement (Score / Target) */}
                <span className="text-[20px] sm:text-[24px] md:text-[26px] font-black text-amber-400 group-hover:text-amber-300 transition-colors tracking-tight px-1 drop-shadow-[0_2px_8px_rgba(245,158,11,0.6)] leading-tight my-0.5 whitespace-nowrap">
                    {line1}
                </span>
                {/* Line 2: Name */}
                <span className="text-[14px] sm:text-[15px] font-black text-slate-50 tracking-wide px-1 drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                    {line2}
                </span>
                {/* Line 3: School & Grade */}
                <span className="text-[11px] sm:text-[12px] font-bold text-slate-400/90 px-1">
                    {line3}
                </span>
            </div>
        </div>
    );
};
